import React from 'react'

import useCountdown from '../../../hooks/useCountdown'

import { CartReservationProgressState } from './useCartReservationProgress'
import * as SC from './CartReservationCountdownStyles'

type CartReservationCountdownSkeletonProps = {
	edgeOffset?: boolean
}

const clampCartExpiresIn = (expiresIn: number | undefined, cartExpirationSeconds: number) => {
	if (expiresIn == null || expiresIn < 0) {
		return cartExpirationSeconds
	}

	return Math.min(expiresIn, cartExpirationSeconds)
}

export const CartReservationCountdownSkeleton = (props: CartReservationCountdownSkeletonProps) => {
	const { edgeOffset } = props

	return (
		<SC.ProgressSection $edgeOffset={edgeOffset}>
			<SC.ProgressTrackSkeleton animated />
			<SC.ProgressHeader>
				<SC.ProgressTitleSkeleton animated />
				<SC.ProgressTimeSkeleton animated />
			</SC.ProgressHeader>
		</SC.ProgressSection>
	)
}

type CartReservationCountdownContentProps = {
	cartExpirationSeconds: number
	expiresInSeconds: number
	onExpired: () => void
	title: string
	edgeOffset?: boolean
	description?: string
}

const CartReservationCountdownContent = (props: CartReservationCountdownContentProps) => {
	const { cartExpirationSeconds, expiresInSeconds, onExpired, title, description, edgeOffset } = props
	const clampedExpiresIn = clampCartExpiresIn(expiresInSeconds, cartExpirationSeconds)
	const { timeRemaining } = useCountdown(onExpired, clampedExpiresIn)
	const progressPercentage = Math.max((timeRemaining / Math.max(cartExpirationSeconds, 1)) * 100, 0)
	const formattedCountdown = `${String(Math.floor(timeRemaining / 60)).padStart(2, '0')}:${String(timeRemaining % 60).padStart(2, '0')}`

	return (
		<SC.ProgressSection $edgeOffset={edgeOffset}>
			<SC.ProgressTrack>
				<SC.ProgressFill $progress={progressPercentage} />
			</SC.ProgressTrack>
			<SC.ProgressHeader>
				<SC.ProgressTitle>{title}</SC.ProgressTitle>
				<SC.ProgressTime>{formattedCountdown}</SC.ProgressTime>
			</SC.ProgressHeader>
			{description && <SC.ProgressDescription>{description}</SC.ProgressDescription>}
		</SC.ProgressSection>
	)
}

type CartReservationCountdownProps = {
	cartExpirationSeconds: number
	expiresInSeconds?: number
	progressState: CartReservationProgressState
	onExpired: () => void
	title: string
	description?: string
	edgeOffset?: boolean
}

const CartReservationCountdown = (props: CartReservationCountdownProps) => {
	const { cartExpirationSeconds, expiresInSeconds, progressState, onExpired, title, description, edgeOffset } = props

	if (progressState === 'hidden') {
		return null
	}

	if (progressState === 'loading' || expiresInSeconds == null) {
		return <CartReservationCountdownSkeleton edgeOffset={edgeOffset} />
	}

	return (
		<CartReservationCountdownContent
			key={clampCartExpiresIn(expiresInSeconds, cartExpirationSeconds)}
			cartExpirationSeconds={cartExpirationSeconds}
			expiresInSeconds={expiresInSeconds}
			onExpired={onExpired}
			title={title}
			description={description}
			edgeOffset={edgeOffset}
		/>
	)
}

export default CartReservationCountdown
